// Slide-out cart. Engraving add-ons are nested child lines of their product; the row carries every key of the group, so quantity changes and removals send them in one /cart/update.js request.
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.queue = Promise.resolve();
    this.timers = new Map();
    this.sectionsUrl = this.dataset.sectionsUrl || '/';
    this.onKeydown = (event) => event.code === 'Escape' && this.close();

    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay' || event.target.closest('[data-cart-drawer-close]')) return this.close();
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) this.changeQuantity(remove.closest('.cart-drawer__item'), 0, remove);
      const edit = event.target.closest('[data-wal-edit-engraving]');
      if (edit && !event.button && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        this.editEngraving(edit);
      }
    });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('.quantity__input')) return;
      this.changeQuantity(event.target.closest('.cart-drawer__item'), parseInt(event.target.value, 10), event.target);
    });
    this.addEventListener(
      'input',
      debounce((event) => {
        if (event.target.id !== 'CartDrawer-Note') return;
        fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ note: event.target.value }) });
      }, ON_CHANGE_DEBOUNCE_TIMER)
    );

    document.addEventListener('click', (event) => {
      const link = event.target.closest('#cart-icon-bubble');
      if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (window.location.pathname === routes.cart_url) return;
      event.preventDefault();
      this.open(link);
    });
    document.addEventListener('keydown', (event) => {
      const link = event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!link || event.code !== 'Space' || window.location.pathname === routes.cart_url) return;
      event.preventDefault();
      this.open(link);
    });
    const link = document.getElementById('cart-icon-bubble');
    if (link) {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer' || event.source === 'product-form') return;
      this.stale = true;
      if (this.classList.contains('active')) return this.refresh().catch(console.error);
    });
  }

  get inner() {
    return this.querySelector('.drawer__inner');
  }

  open(trigger) {
    if (trigger) this.setActiveElement(trigger);
    if (this.stale) {
      this.stale = false;
      this.refresh().catch(console.error);
    }
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeydown);
    requestAnimationFrame(() => trapFocus(this.inner, this.inner));
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeydown);
    removeTrapFocus(this.activeElement);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
  }

  async fetchSections() {
    const response = await fetch(`${this.sectionsUrl}?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Could not load the cart (${response.status})`);
    return response.json();
  }

  async refresh() {
    this.applySections(await this.fetchSections());
  }

  applySections(sections, announce) {
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    const next = sections['cart-drawer'] && parse(sections['cart-drawer']).querySelector('cart-drawer');
    if (next && this.inner) {
      const focused = this.rowFocus();
      this.inner.innerHTML = next.querySelector('.drawer__inner').innerHTML;
      this.classList.toggle('is-empty', next.classList.contains('is-empty'));
      if (this.classList.contains('active')) this.restoreFocus(focused);
      if (announce) this.announce();
    }
    const bubble = document.getElementById('cart-icon-bubble');
    const nextBubble = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
    if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
  }

  rowFocus() {
    const active = document.activeElement;
    const row = active && this.contains(active) && active.closest('.cart-drawer__item');
    if (!row) return null;
    const control = active.matches('.quantity__button') ? `[name="${active.name}"]` : active.matches('[data-cart-remove]') ? '[data-cart-remove]' : '.quantity__input';
    return { key: row.dataset.keys.split(' ')[0], control };
  }

  restoreFocus(focused) {
    const row = focused && [...this.querySelectorAll('.cart-drawer__item')].find((item) => item.dataset.keys.split(' ')[0] === focused.key);
    trapFocus(this.inner, (row && row.querySelector(focused.control)) || this.querySelector('.drawer__close') || this.inner);
  }

  announce() {
    const live = this.querySelector('#CartDrawer-LiveRegionText');
    const totals = this.querySelector('.cart-drawer__totals');
    if (live) live.textContent = totals ? totals.textContent.replace(/\s+/g, ' ').trim() : this.querySelector('.cart-drawer__empty-title')?.textContent.trim() || '';
  }

  // Called by product-form.js once an item is added: fly the product image to the cart icon, update the bubble, open the drawer with fresh contents.
  renderContents(state, { published, origin } = {}) {
    const flight = this.fly(origin);
    return Promise.resolve(published)
      .then(() => this.fetchSections())
      .then(async (sections) => {
        await flight;
        this.applySections(sections);
        this.bump();
        this.open();
      })
      .catch((error) => {
        console.error(error);
        window.location = routes.cart_url;
      });
  }

  loadFlyToCart() {
    if (!this.flyReady) {
      const load = (element) => new Promise((resolve) => {
        element.addEventListener('load', resolve);
        element.addEventListener('error', resolve);
        document.head.append(element);
      });
      const style = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: this.dataset.flyStyle });
      const script = Object.assign(document.createElement('script'), { src: this.dataset.flyScript });
      this.flyReady = customElements.get('fly-to-cart') ? Promise.resolve() : Promise.all([load(style), load(script)]);
    }
    return this.flyReady;
  }

  async fly(origin) {
    const icon = document.getElementById('cart-icon-bubble');
    if (!origin || !icon || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      await Promise.race([this.loadFlyToCart(), new Promise((resolve) => setTimeout(resolve, 400))]);
      if (!customElements.get('fly-to-cart')) return;
      const destination = icon.getBoundingClientRect();
      if (destination.bottom < 0 || destination.top > window.innerHeight) return;
      const image = origin.image && origin.image.getBoundingClientRect();
      const visible = image && Math.min(image.bottom, window.innerHeight) - Math.max(image.top, 0) > image.height * 0.4;
      const flyer = document.createElement('fly-to-cart');
      document.body.append(flyer);
      await flyer.fly({
        from: visible ? image : origin.button.getBoundingClientRect(),
        to: destination,
        src: origin.image && (origin.image.currentSrc || origin.image.src),
        fromImage: visible,
      });
    } catch (error) {
      console.error(error);
    }
  }

  bump() {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    icon.classList.remove('fly-to-cart-bump');
    void icon.offsetWidth;
    icon.classList.add('fly-to-cart-bump');
    icon.addEventListener('animationend', () => icon.classList.remove('fly-to-cart-bump'), { once: true });
  }

  loadEngravingEditor() {
    if (!this.editReady) {
      const load = (element) => new Promise((resolve, reject) => {
        element.addEventListener('load', resolve);
        element.addEventListener('error', () => reject(new Error(`Could not load ${element.src || element.href}`)));
        document.head.append(element);
      });
      const style = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: this.dataset.editStyle });
      const script = Object.assign(document.createElement('script'), { src: this.dataset.editScript });
      this.editReady = (window.walEngravingEditor ? Promise.resolve() : Promise.all([load(style), load(script)])).catch((error) => {
        this.editReady = null;
        throw error;
      });
    }
    return this.editReady;
  }

  // Loads the words editor on the first click; without it the link still goes to the cart page.
  async editEngraving(link) {
    const row = link.closest('.cart-drawer__item');
    const key = row.dataset.keys.split(' ')[0];
    try {
      await this.loadEngravingEditor();
      const response = await fetch('/cart.js', { cache: 'no-store' });
      if (!response.ok) throw new Error(`Could not load the cart (${response.status})`);
      const cart = await response.json();
      const item = cart.items.find((line) => line.key === key);
      if (!item || !item.properties?.Text) return this.refresh();
      const dl = row.querySelector('.cart-drawer__props');
      if (row.querySelector('.wal-cart-edit')) return;
      const editor = window.walEngravingEditor.open({
        anchor: dl || link,
        item,
        onSaved: async (state) => {
          await this.refresh();
          await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state });
          const next = this.querySelector(`#${row.id} [data-wal-edit-engraving]`) || this.querySelector(`#${row.id} .quantity__input`);
          if (next) next.focus();
        },
        onCancel: () => {
          link.hidden = false;
          link.focus();
        },
      });
      if (editor) link.hidden = true;
    } catch (error) {
      console.error(error);
      window.location = link.href;
    }
  }

  changeQuantity(row, quantity, control) {
    if (!row || Number.isNaN(quantity)) return;
    const keys = row.dataset.keys.split(' ');
    const quantities = row.dataset.quantities.split(' ').map(Number);
    const first = keys[0];
    clearTimeout(this.timers.get(first));
    this.timers.set(
      first,
      setTimeout(() => {
        this.timers.delete(first);
        const updates = {};
        keys.forEach((key, index) => (updates[key] = Math.round((quantities[index] * quantity) / quantities[0])));
        this.queue = this.queue.then(() => this.update(row, updates, control)).catch(console.error);
      }, quantity === 0 ? 0 : ON_CHANGE_DEBOUNCE_TIMER)
    );
  }

  async update(row, updates, control) {
    const items = this.querySelector('.cart-drawer__items');
    if (items) items.classList.add('is-loading');
    try {
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ updates, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname }),
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw new Error(state.description || state.message || state.errors);
      this.applySections(state.sections, true);
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state });
      const sections = await this.fetchSections();
      const current = this.inner.innerHTML;
      const next = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('.drawer__inner');
      if (next && next.innerHTML !== current) this.applySections(sections, true);
    } catch (error) {
      console.error(error);
      const message = typeof error.message === 'string' && error.message ? error.message : window.cartStrings.error;
      const target = this.querySelector(`[data-keys^="${Object.keys(updates)[0]}"] .cart-drawer__error`);
      if (target) target.textContent = message;
      const live = this.querySelector('#CartDrawer-LiveRegionText');
      if (live) live.textContent = message;
      if (items) items.classList.remove('is-loading');
      const input = control && control.matches && control.matches('.quantity__input') ? control : null;
      if (input) input.value = row.dataset.quantities.split(' ')[0];
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
